<script setup lang="ts">
import { computed, inject, ref, toRef } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { GaugeChart, BarChart, PieChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import { useI18n } from '@/composable/useI18n'
import { getWidgetDensity, useCustomDashboardWidget } from '@/composables/useCustomDashboardWidget'
import { isGroupWidget } from '@/utils/dashboard/widget-source'
import { useResponsiveGaugeChart } from '@/composables/widgets/useResponsiveGaugeChart'
import DashboardWidgetCardShell from './DashboardWidgetCardShell.vue'
import WidgetLayouts from './WidgetLayouts.vue'
import DashboardStatusPanelWidget from '@/components/Dashboard/Widgets/DashboardStatusPanelWidget.vue'
import DashboardTimeSeriesWidget from '@/components/Dashboard/Widgets/DashboardTimeSeriesWidget.vue'
import DashboardOpenAlarmsWidget from '@/components/Dashboard/Widgets/DashboardOpenAlarmsWidget.vue'
import DashboardPieChartWidget from '@/components/Dashboard/Widgets/DashboardPieChartWidget.vue'
import DashboardBarChartWidget from '@/components/Dashboard/Widgets/DashboardBarChartWidget.vue'
import DashboardTotalizersWidget from '@/components/Dashboard/Widgets/DashboardTotalizersWidget.vue'
import type { DashboardSensorLastReadingsContext } from '@/composables/useDashboardSensorLastReadings'

use([
  CanvasRenderer,
  GaugeChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
])

const props = defineProps<{
  widget: IWidget
  editMode: boolean
  sensor: ISensor | undefined
}>()

const emit = defineEmits<{
  edit: [widget: IWidget]
  remove: [id: string]
}>()

const { t } = useI18n()
const {
  getSensorName,
  getSecondarySensorForWidget,
  getStatusPanelBorderColor,
  getTimeSeriesBorderColor,
  getWidgetAccentColor,
  getWidgetBorderColor,
  getIndicatorValue,
  getIndicatorUnit,
  getAggregationBadgeLabel,
} = useCustomDashboardWidget()

const secondarySensor = computed(() => getSecondarySensorForWidget(props.widget))

const widgetDensity = computed(() => getWidgetDensity(props.widget))

const gaugeChartContainerRef = ref<HTMLElement | null>(null)
const { chartOption: gaugeChartOption } = useResponsiveGaugeChart(
  toRef(props, 'widget'),
  gaugeChartContainerRef,
)

const indicatorValueClass = computed(() => {
  if (widgetDensity.value === 'dense') return 'font-satoshi text-2xl font-bold leading-none text-black'
  if (widgetDensity.value === 'compact') return 'font-satoshi text-4xl font-bold leading-none text-black'
  return 'font-satoshi text-5xl font-bold leading-none text-black'
})

const indicatorUnitClass = computed(() => {
  if (widgetDensity.value === 'dense') return 'text-sm font-medium text-body'
  if (widgetDensity.value === 'compact') return 'text-lg font-medium text-body'
  return 'text-xl font-medium text-body'
})

const indicatorBadgeClass = computed(() => {
  if (widgetDensity.value === 'dense') {
    return 'mt-1 text-2xs font-medium uppercase tracking-wide text-blue'
  }
  if (widgetDensity.value === 'compact') {
    return 'mt-2 text-2xs font-medium uppercase tracking-wide text-blue'
  }
  return 'mt-3 text-xs font-medium uppercase tracking-wide text-blue'
})

const indicatorContentClass = computed(() => {
  if (widgetDensity.value === 'dense') return 'items-center justify-center px-2 pb-2'
  if (widgetDensity.value === 'compact') return 'items-center justify-center px-3 pb-2.5'
  return 'items-center justify-center px-4 pb-3'
})

const lastReadingsContext = inject<DashboardSensorLastReadingsContext | null>(
  'dashboardSensorLastReadings',
  null,
)

const staleDataNoticeProps = computed(() => lastReadingsContext?.getNoticeProps(props.widget) ?? null)

const showStaleDataNotice = computed(() => staleDataNoticeProps.value != null)

const openAlarmsSubtitle = computed(() => t('custom_dashboards.view.open_alarms.subtitle'))

const shellBorderColor = computed(() => {
  const { widget_type } = props.widget
  if (widget_type === 'gauge') return getWidgetBorderColor(props.widget)
  if (widget_type === 'status-panel') return getStatusPanelBorderColor(props.widget)
  if (widget_type === 'time-series') return getTimeSeriesBorderColor(props.widget)
  return getWidgetAccentColor(widget_type)
})

const shellBorderWidth = computed(() =>
  props.widget.widget_type === 'pie-chart' || props.widget.widget_type === 'bar-chart' ? 3 : 4,
)

const shellShadow = computed(() =>
  props.widget.widget_type === 'pie-chart' || props.widget.widget_type === 'bar-chart' ? 'md' : 'sm',
)

function onEdit() {
  emit('edit', props.widget)
}

function onRemove() {
  emit('remove', props.widget.id)
}
</script>

<template>
  <DashboardWidgetCardShell
    :border-color="shellBorderColor"
    :border-width="shellBorderWidth"
    :shadow="shellShadow"
    :edit-mode="editMode"
    @edit="onEdit"
    @remove="onRemove"
  >
    <WidgetLayouts
      v-if="widget.widget_type === 'indicator'"
      variant="centered"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :edit-mode="editMode"
      :density="widgetDensity"
      :content-class="indicatorContentClass"
    >
      <div class="flex min-w-0 items-baseline justify-center gap-1">
        <span :class="indicatorValueClass">
          {{ getIndicatorValue(widget) }}
        </span>
        <span :class="indicatorUnitClass">{{ getIndicatorUnit(widget) }}</span>
      </div>
      <p :class="indicatorBadgeClass">
        {{
          isGroupWidget(widget)
            ? t('custom_dashboards.widget_modal.common.source_types.group')
            : getAggregationBadgeLabel(widget)
        }}
      </p>
      <p
        v-if="showStaleDataNotice && staleDataNoticeProps?.error"
        class="mt-1 text-center text-2xs font-medium leading-snug text-red-700"
      >
        {{ staleDataNoticeProps.error }}
      </p>
      <p
        v-else-if="showStaleDataNotice && staleDataNoticeProps"
        class="mt-1 text-center text-2xs font-medium leading-snug text-amber-600"
      >
        {{ staleDataNoticeProps.message }}
      </p>
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'totalizers'"
      variant="centered"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :edit-mode="editMode"
      :density="widgetDensity"
      :content-class="indicatorContentClass"
    >
      <DashboardTotalizersWidget :widget="widget" :density="widgetDensity" />
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'gauge'"
      variant="centered"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :edit-mode="editMode"
      :density="widgetDensity"
      content-class="flex min-h-0 flex-1 flex-col px-2 pb-2 pt-0"
    >
      <div ref="gaugeChartContainerRef" class="flex min-h-0 w-full flex-1 flex-col">
        <VChart
          :option="gaugeChartOption"
          class="min-h-[40px] w-full flex-1"
          autoresize
        />
        <p
          v-if="showStaleDataNotice && staleDataNoticeProps?.error"
          class="mt-auto shrink-0 pt-1 text-center text-2xs font-medium leading-snug text-red-700"
        >
          {{ staleDataNoticeProps.error }}
        </p>
        <p
          v-else-if="showStaleDataNotice && staleDataNoticeProps"
          class="mt-auto shrink-0 pt-1 text-center text-2xs font-medium leading-snug text-amber-600"
        >
          {{ staleDataNoticeProps.message }}
        </p>
      </div>
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'status-panel'"
      variant="split-header"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :edit-mode="editMode"
      :density="widgetDensity"
    >
      <DashboardStatusPanelWidget :widget="widget" :density="widgetDensity" />
      <p
        v-if="showStaleDataNotice && staleDataNoticeProps?.error"
        class="mt-1 px-3 pb-2 text-center text-2xs font-medium leading-snug text-red-700"
      >
        {{ staleDataNoticeProps.error }}
      </p>
      <p
        v-else-if="showStaleDataNotice && staleDataNoticeProps"
        class="mt-1 px-3 pb-2 text-center text-2xs font-medium leading-snug text-amber-600"
      >
        {{ staleDataNoticeProps.message }}
      </p>
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'time-series'"
      variant="split-header"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :edit-mode="editMode"
      content-class="px-2 pb-2 pt-1"
    >
      <DashboardTimeSeriesWidget
        :widget="widget"
        :sensor="sensor"
        :secondary-sensor="secondarySensor"
      />
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'open-alarms'"
      variant="split-header"
      :title="widget.title"
      :subtitle="openAlarmsSubtitle"
      :edit-mode="editMode"
    >
      <DashboardOpenAlarmsWidget />
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'pie-chart'"
      variant="dot-with-subtitle"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :accent-color="getWidgetAccentColor(widget.widget_type)"
      :edit-mode="editMode"
      content-class="px-1 pb-1 pt-0"
    >
      <DashboardPieChartWidget :widget="widget" />
    </WidgetLayouts>

    <WidgetLayouts
      v-else-if="widget.widget_type === 'bar-chart'"
      variant="dot-with-subtitle"
      :title="widget.title"
      :subtitle="getSensorName(widget)"
      :accent-color="getWidgetAccentColor(widget.widget_type)"
      :edit-mode="editMode"
      content-class="px-2 pb-2 pt-1"
    >
      <DashboardBarChartWidget :widget="widget" :sensor="sensor" />
    </WidgetLayouts>
  </DashboardWidgetCardShell>
</template>
